import * as React from "react"
import { Send, X } from "lucide-react"

import { useBackToClose } from "@/lib/use-back-to-close"
import { cn } from "@/lib/utils"
import { Button } from "@/shadcn/components/ui/button"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogNonModalOverlay,
  DialogPortal,
  DialogTitle,
} from "@/shadcn/components/ui/dialog"

/**
 * The "you did the thing" card shown once, after a new account's first post or
 * first automation. Figma: S311lGXE6JSyTnNAXHmtR7, frame 29821-113548.
 *
 * The copy differs per milestone and lives with the caller, which is also what
 * knows where each button goes — this file owns the card and nothing else.
 *
 * The primary button invites them to do it again and the ghost button leads to
 * where the thing they just made now lives. The Figma frame draws that emphasis
 * the other way round; the labels and their weights are the caller's to decide.
 *
 * Open is not a state: the legacy modal stack mounts this to show it and
 * unmounts it to close it, so mounted IS open — see ConnectSocialDialog, whose
 * non-modal setup this follows for the same reason (Radix's `modal` would put
 * `pointer-events: none` on <body> and kill any legacy modal raised over it).
 */

type FirstSuccessDialogProps = {
  headline: string
  subhead: string
  primaryLabel: string
  secondaryLabel: string
  onPrimary: () => void
  onSecondary: () => void
  onClose: () => void
  className?: string
}

export default function FirstSuccessDialog({
  headline,
  subhead,
  primaryLabel,
  secondaryLabel,
  onPrimary,
  onSecondary,
  onClose,
  className,
}: FirstSuccessDialogProps) {
  // The scroll lock Radix would have given us, declined above with modal={false}.
  React.useEffect(() => {
    document.body.classList.add("modal-open")

    return () => {
      document.body.classList.remove("modal-open")
    }
  }, [])

  // Back dismisses this, as it does every legacy modal.
  useBackToClose(onClose)

  return (
    <Dialog
      open
      modal={false}
      onOpenChange={(open) => {
        if (!open) onClose()
      }}
    >
      {/* Radix renders no overlay for a non-modal dialog; this puts the scrim
          back and is also what closes the card on a click beside it. */}
      <DialogPortal>
        <DialogNonModalOverlay onClick={onClose} />
      </DialogPortal>
      <DialogContent
        showCloseButton={false}
        /* The scrim above owns outside-dismissal. Radix's own detection misfires
           through this portal with modal={false} — see ConnectSocialDialog. */
        onInteractOutside={(event) => {
          event.preventDefault()
        }}
        className={cn(
          "tw:flex tw:flex-col tw:items-center tw:gap-7 tw:rounded-2xl tw:border tw:border-border tw:bg-card tw:p-6 tw:text-card-foreground tw:shadow-xs tw:ring-0 tw:sm:max-w-[440px]",
          className
        )}
      >
        <div className="tw:flex tw:w-full tw:items-center">
          {/* the frame's "spring": the X is right-aligned in a row of its own */}
          <div className="tw:h-px tw:flex-1" />
          <Button
            variant="ghost"
            size="icon-sm"
            aria-label="Close"
            onClick={onClose}
            className="tw:shrink-0"
          >
            <X className="tw:size-4" />
          </Button>
        </div>

        <div className="tw:flex tw:size-16 tw:shrink-0 tw:items-center tw:justify-center tw:rounded-full tw:bg-blue-100">
          <Send className="tw:size-7 tw:text-primary" strokeWidth={1.5} />
        </div>

        <div className="tw:flex tw:w-full tw:flex-col tw:items-center tw:gap-2 tw:text-center">
          <DialogTitle className="tw:text-2xl tw:leading-8 tw:font-semibold tw:text-foreground">
            {headline}
          </DialogTitle>
          <DialogDescription className="tw:leading-5">
            {subhead}
          </DialogDescription>
        </div>

        <div className="tw:flex tw:w-full tw:flex-col tw:gap-2">
          <Button size="lg" className="tw:w-full" onClick={onPrimary}>
            {primaryLabel}
          </Button>
          <Button
            variant="ghost"
            size="lg"
            className="tw:w-full"
            onClick={onSecondary}
          >
            {secondaryLabel}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  )
}
